import React, { useEffect, useMemo, useState } from 'react';
import cx from 'classnames';
import { Link } from 'react-router-dom';
import { RaceTypeAll } from '@apollo/core/src/constants';
import { SiteConfigManager } from '@apollo/core';
import { COMPONENT_TYPES } from '@apollo/routing';
import RaceLink from '../../../shared/components/Racing/RaceLink';
import Dropdown from '../../../shared/components/Dropdown/Dropdown';
import { AnimatedDropdown } from '../../Animations/AnimatedDropdown';
import useScrollPage from '../../../shared/components/Dropdown/useScrollPage';
import { generateRaceLink } from '../../../core/utils';
import { RACE_STATUS, specialDates } from '../../../core/constants';
import { nextToJumpPath } from '../RacingPathHandler';
import Label from '../../../shared/components/I18n/Label';
import { BonusSelector } from '../../../state/Bonus/Bonus';
import useTranslate from '../../../shared/components/I18n/Interpreter';

const RaceListDropdown = ({ meetings, activeMeet, children }) => {
  const [isShowRaceList, setIsShowRaceList] = useState(false);
  const t = useTranslate();

  const racingPath = SiteConfigManager.getExtraConfig(`pathList.${COMPONENT_TYPES.RACING}`);

  const todayRaceUrl = generateRaceLink({ raceDate: specialDates.Today });

  const meetingsList = useMemo(() => {
    if (activeMeet == null) {
      return [];
    }

    const meetingByType = (meetings || [])
      .filter(({ type, date }) => type === activeMeet.type && date === activeMeet.date)
      .map((meet) => ({
        ...meet,
        hasPromoOdds: meet.races.find(
          (race) => race.status === RACE_STATUS.OPEN && BonusSelector.hasActivePromoOdds(race?.raceId),
        ),
      }));

    return meetingByType.sort((a, b) => {
      const priority = (country) => {
        if (country === 'NZL') return 0;
        if (country === 'AUS') return 1;
        return 2;
      };

      const priorityA = priority(a.country);
      const priorityB = priority(b.country);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      // If both are in the same priority group, sort alphabetically by name
      /*  if (a.venueName < b.venueName) return -1;
      if (a.venueName > b.venueName) return 1; */
      return a?.country;
    });
  }, [meetings, activeMeet]);

  const isScroll = useScrollPage();

  useEffect(() => {
    if (isScroll) {
      setIsShowRaceList(false);
    }
  }, [isScroll]);

  return (
    <Dropdown
      isExpanded={isShowRaceList}
      onExpanded={setIsShowRaceList}
      className='race-list-dropdown'
    >
      <div
        className={cx('race-name', { expanded: isShowRaceList })}
        title={activeMeet.venue.venueName}
      >
        {children}
      </div>
      <AnimatedDropdown
        isExpanded={isShowRaceList}
        className='race-list race-list__dropdown'
        endOverflow='auto'
      >
        <div className='race-list-item race-list-item--today'>
          <Link to={todayRaceUrl}>
            <Label message={RaceTypeAll.typeName} />
          </Link>
        </div>
        <div className='race-list-item race-list-item--next-to-jump'>
          <Link to={`${racingPath}${nextToJumpPath}`}>
            <Label message='next_to_jump' />
          </Link>
        </div>
        {meetingsList.map((meeting) => (
          <div
            key={meeting.meetingId}
            className={cx([
              'race-list-item',
              'race-list-item--race',
              meeting.meetingId === activeMeet.meetingId && ' active',
            ])}
          >
            <RaceLink
              raceDate={meeting.date}
              raceType={meeting.raceType.typeName}
              raceCountry={meeting.country}
              raceVenue={meeting.venueName}
              raceNumber={
                meeting.races.find((r) => r.status === RACE_STATUS.OPEN)?.number
                || meeting.races[0]?.number
              }
            >
              {meeting.venueName}
              {meeting.hasPromoOdds ? (
                <i className='icons icons icon--promo-odds' title={t('promo odds')} />
              ) : null}
            </RaceLink>
          </div>
        ))}
      </AnimatedDropdown>
    </Dropdown>
  );
};

export default RaceListDropdown;
